import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { HttpService } from '../../../core/http/http-service';
import { CompanyContextService } from '../../../core/services/company-context.service';
import { formatDecimalForFormData } from '../../../shared/utils/api-number.util';
import {
  CreateExpenseRequest,
  ExpenseDetailDto,
  ExpenseListFilter,
  ExpenseListResponse,
  SetExpensePaymentRequest,
  UpdateExpenseRequest,
  paymentChannelLabel,
  paymentModeLabel,
  paymentStateLabel,
  expenseStatusLabel,
} from '../models/expenses.models';

/** Raw shape coming from the API. */
interface ApiExpenseDto {
  publicId: string;
  expenseCategoryPublicId: string;
  expenseCategoryName: string;
  categoryGroupName: string;
  transactionDate: string;
  documentNo: string;
  dueDate?: string;
  projectName?: string;
  accountName: string;
  accountPublicId?: string;
  amountGross: number;
  vatRate: number;
  netAmount: number;
  currencyId: number;
  currencyCode: string;
  exchangeRate?: number | null;
  vatExemptionTypeId?: number | null;
  exemptionCode?: string | null;
  exemptionReason?: string | null;
  amountPaid?: number | null;
  amountDue?: number | null;
  lastPaymentDate?: string | null;
  paymentMode: number;
  paymentState: number;
  paymentChannel: number;
  status: number;
  recurringEnabled: boolean;
  hasAttachment: boolean;
  attachmentName?: string;
  description?: string;
  note?: string;
  paymentDate?: string;
  isActive: boolean;
  isCancelled?: boolean;
  dtInsert?: string;
  dtUpdate?: string;
  source:string;
}

interface ApiExpenseListEnvelope {
  items: ApiExpenseDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class ExpensesService {
  private readonly http = inject(HttpService);
  private readonly companyContext = inject(CompanyContextService);

  list(filter: ExpenseListFilter): Observable<ExpenseListResponse> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    return this.http
      .get<ApiExpenseListEnvelope>(
        `/api/companies/${encodeURIComponent(companyId)}/expenses`,
        { params: this.toListParams(filter) },
      )
      .pipe(
        map((res) => ({
          items: (res.items ?? []).map((row) => this.mapExpense(row)),
          totalCount: Number(res.totalCount ?? 0),
          page: Number(res.page ?? filter.page ?? 1),
          pageSize: Number(res.pageSize ?? filter.pageSize ?? 10),
        })),
      );
  }

  getById(id: string): Observable<ExpenseDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    return this.http
      .get<ApiExpenseDto>(
        `/api/companies/${encodeURIComponent(companyId)}/expenses/${encodeURIComponent(id)}`,
      )
      .pipe(map((row) => this.mapExpense(row)));
  }

  create(payload: CreateExpenseRequest, file?: File): Observable<ExpenseDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    const formData = this.buildFormData(payload, file);

    return this.http
      .post<ApiExpenseDto, FormData>(
        `/api/companies/${encodeURIComponent(companyId)}/expenses`,
        formData,
      )
      .pipe(map((row) => this.mapExpense(row)));
  }

  update(id: string, payload: UpdateExpenseRequest, file?: File): Observable<ExpenseDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    const formData = this.buildFormData(payload, file);

    return this.http
      .put<ApiExpenseDto, FormData>(
        `/api/companies/${encodeURIComponent(companyId)}/expenses/${encodeURIComponent(id)}`,
        formData,
      )
      .pipe(map((row) => this.mapExpense(row)));
  }

  delete(id: string): Observable<void> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    return this.http.delete<void>(
      `/api/companies/${encodeURIComponent(companyId)}/expenses/${encodeURIComponent(id)}`,
    );
  }

  setPayment(id: string, payload: SetExpensePaymentRequest): Observable<void> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    return this.http.post<void, SetExpensePaymentRequest>(
      `/api/companies/${encodeURIComponent(companyId)}/expenses/${encodeURIComponent(id)}/set-payment`,
      payload,
    );
  }

  quickUpload(file: File): Observable<unknown> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    const fd = new FormData();
    fd.append('file', file, file.name);

    return this.http.post<unknown, FormData>(
      `/api/companies/${encodeURIComponent(companyId)}/expenses/quick-upload`,
      fd,
    );
  }

  downloadAttachment(id: string): Observable<HttpResponse<Blob>> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) return throwError(() => new Error('Şirket seçimi bulunamadı.'));

    return this.http.getBlobResponse(
      `/api/companies/${encodeURIComponent(companyId)}/expenses/${encodeURIComponent(id)}/attachment`,
    );
  }

  // ── Private helpers ─────────────────────────────────────────────

  private resolveCompanyIdOrThrow(): string | null {
    const companyId = this.companyContext.getSelectedCompanyId();
    return companyId && companyId.trim() ? companyId.trim() : null;
  }

  private toListParams(filter: ExpenseListFilter): Record<string, string | number | boolean> {
    const params: Record<string, string | number | boolean> = {
      Page: filter.page ?? 1,
      PageSize: filter.pageSize ?? 10,
    };

    if (filter.status !== undefined && filter.status !== null) params['Status'] = filter.status;
    if (filter.paymentState !== undefined && filter.paymentState !== null) params['PaymentState'] = filter.paymentState;
    if (filter.transactionDateFrom) params['TransactionDateFrom'] = filter.transactionDateFrom;
    if (filter.transactionDateTo) params['TransactionDateTo'] = filter.transactionDateTo;
    if (filter.search?.trim()) params['Search'] = filter.search.trim();
    if (filter.sortBy?.trim()) params['SortBy'] = filter.sortBy.trim();
    if (filter.sortDirection) params['SortDirection'] = filter.sortDirection;
    if (filter.expenseCategoryPublicId?.trim()) params['ExpenseCategoryPublicId'] = filter.expenseCategoryPublicId.trim();

    return params;
  }

  private buildFormData(payload: CreateExpenseRequest | UpdateExpenseRequest, file?: File): FormData {
    const fd = new FormData();

    fd.append('ExpenseCategoryPublicId', payload.expenseCategoryPublicId);
    fd.append('TransactionDate', payload.transactionDate);
    fd.append('AccountName', payload.accountName);
    fd.append('AmountGross', formatDecimalForFormData(payload.amountGross));
    fd.append('VatRate', formatDecimalForFormData(payload.vatRate));
    fd.append('NetAmount', formatDecimalForFormData(payload.netAmount));
    fd.append('CurrencyId', String(payload.currencyId));
    if (payload.exchangeRate != null) fd.append('ExchangeRate', formatDecimalForFormData(payload.exchangeRate));
    if (payload.vatExemptionTypeId != null) fd.append('VatExemptionTypeId', String(payload.vatExemptionTypeId));
    fd.append('PaymentMode', String(payload.paymentMode));
    fd.append('PaymentState', String(payload.paymentState));
    fd.append('PaymentChannel', String(payload.paymentChannel));
    fd.append('Status', String(payload.status));
    fd.append('RecurringEnabled', String(payload.recurringEnabled));

    if (payload.documentNo) fd.append('DocumentNo', payload.documentNo);
    if (payload.dueDate) fd.append('DueDate', payload.dueDate);
    if (payload.paymentDate) fd.append('PaymentDate', payload.paymentDate);
    if (payload.projectName) fd.append('ProjectName', payload.projectName);
    if (payload.accountPublicId) fd.append('AccountPublicId', payload.accountPublicId);
    if (payload.description) fd.append('Description', payload.description);
    if (payload.note) fd.append('Note', payload.note);

    if ('isActive' in payload) {
      fd.append('IsActive', String((payload as UpdateExpenseRequest).isActive));
    }

    if (file) {
      fd.append('attachment', file, file.name);
    }

    return fd;
  }

  private mapExpense(row: ApiExpenseDto): ExpenseDetailDto {
    const categoryPath = [row.categoryGroupName, row.expenseCategoryName].filter(Boolean).join(' / ') || 'Tanımsız Kategori';

    return {
      publicId: row.publicId,
      id: row.publicId,
      expenseCategoryPublicId: row.expenseCategoryPublicId,
      expenseCategoryName: row.expenseCategoryName,
      categoryGroupName: row.categoryGroupName,
      categoryPath,
      transactionDate: row.transactionDate,
      documentNo: row.documentNo,
      dueDate: row.dueDate,
      projectName: row.projectName,
      accountName: row.accountName,
      accountPublicId: row.accountPublicId,
      amountGross: row.amountGross,
      vatRate: row.vatRate,
      netAmount: row.netAmount,
      currencyId: row.currencyId,
      currencyCode: row.currencyCode,
      exchangeRate: row.exchangeRate ?? null,
      vatExemptionTypeId: row.vatExemptionTypeId ?? null,
      exemptionCode: row.exemptionCode ?? null,
      exemptionReason: row.exemptionReason ?? null,
      amountPaid: row.amountPaid ?? null,
      amountDue: row.amountDue ?? null,
      lastPaymentDate: row.lastPaymentDate ?? null,
      paymentMode: paymentModeLabel(row.paymentMode),
      paymentState: paymentStateLabel(row.paymentState),
      paymentChannel: paymentChannelLabel(row.paymentChannel),
      status: expenseStatusLabel(row.status),
      recurringEnabled: row.recurringEnabled,
      hasAttachment: row.hasAttachment,
      attachmentName: row.attachmentName,
      description: row.description,
      note: row.note,
      paymentDate: row.paymentDate,
      isActive: row.isActive,
      isCancelled: !!row.isCancelled,
      source: row.source
    };
  }
}
